import { makeAutoObservable } from "mobx";
import { inject, injectable } from "tsyringe";
import { ReplayStore, TOKEN_ReplayStore } from "./utility-stores/ReplayStore";
import { type IProjectSyncing, TOKEN_IProjectSyncing } from "@/services/ProjectSyncing";
import { ProjectAccessStore, TOKEN_ProjectAccessStore } from "./utility-stores/ProjectAccessStore";
import { ProjectBusEvents, TOKEN_IProjectBus, type IProjectBus } from "@/services/ProjectBus";
import { TOKEN_IProjectWorldWriter, type IProjectWorldWriter } from "@/services/ProjectWorldWriter";
import { TOKEN_IProjectWorldReader, type IProjectWorldReader } from "@/services/ProjectWorldReader";
import { hexStringWithFloatAlphaToNumber, numberToHexStringAndAlpha } from "@/lib/utils";

export const TOKEN_TopNavQuickActionsStore = Symbol("TopNavQuickActionsStore");

@injectable()
export class TopNavQuickActionsStore {
    private _colorPickerValue: string = "#000000";
    private _hasUndo: boolean = false;
    private _hasRedo: boolean = false;

    get isUIReadOnly(): boolean { return this.projectAccessStore.currentProjectId === undefined || this.projectAccessStore.isUIReadOnly; }

    get isInHistoryMode(): boolean { return this.replayStore.isInHistoryMode; }

    get colorPickerValue(): string { return this._colorPickerValue; }

    stopReplay = () => this.replayStore.stopReplay();

    get showUndo(): boolean { return !this.isUIReadOnly && this._hasUndo; }

    get showRedo(): boolean { return !this.isUIReadOnly && this._hasRedo; }

    undo = () => this.projectSyncing.undo(this.projectAccessStore.currentProjectId!);

    redo = () => this.projectSyncing.redo(this.projectAccessStore.currentProjectId!);

    colorPickerOnChange = async (value: string) => {
        this._colorPickerValue = value;

        const projectWorld = await this.projectWorldReader.mustGet(this.projectAccessStore.currentProjectId!);
        projectWorld.projectMetadata.myColor = hexStringWithFloatAlphaToNumber(value, 1.0);
        await this.projectWorldWriter.saveProjectWorldMetadata(projectWorld.projectMetadata);
    };

    constructor(
        @inject(TOKEN_ReplayStore) private readonly replayStore: ReplayStore,
        @inject(TOKEN_ProjectAccessStore) private readonly projectAccessStore: ProjectAccessStore,
        @inject(TOKEN_IProjectSyncing) private readonly projectSyncing: IProjectSyncing,
        @inject(TOKEN_IProjectBus) projectBus: IProjectBus,
        @inject(TOKEN_IProjectWorldWriter) private readonly projectWorldWriter: IProjectWorldWriter,
        @inject(TOKEN_IProjectWorldReader) private readonly projectWorldReader: IProjectWorldReader,
    ) {
        makeAutoObservable(this);
        projectBus.subscribe_projectLoaded(this.onProjectLoaded);
        projectBus.subscribe_projectionUpdated(this.onProjectionUpdated);
    }

    onProjectionUpdated = async (): Promise<void> => {
        if (!this.projectAccessStore.currentProjectId) return;
        const projectWorld = await this.projectWorldReader.mustGet(this.projectAccessStore.currentProjectId!);
        this._hasUndo = projectWorld.undoRedoStack.hasUndo;
        this._hasRedo = projectWorld.undoRedoStack.hasRedo;
    }

    private onProjectLoaded = async (data: ProjectBusEvents['PROJECT_LOADED']): Promise<void> => {
        const projectWorld = await this.projectWorldReader.mustGet(data.projectId);
        this._colorPickerValue = numberToHexStringAndAlpha(projectWorld.projectMetadata.myColor).hex;
    }
}